import Container from "@mui/material/Container";
import { useOrbit } from "providers/OrbitProvider";
import CampaignSpend, { GET_ALLOCATION_QUERY } from "./CampaignSpend";
import { action } from "@storybook/addon-actions";
import { ComponentProps } from "react";
import { Meta } from "@storybook/react";
import Suspenseful from "components/Suspenseful";
import { expect, userEvent, screen, within, waitForElementToBeRemoved } from "@storybook/test";


export default {
  title: "Pages/Allocations/CampaignSpend",
  component: CampaignSpend,
};

const Template = (args: ComponentProps<typeof CampaignSpend>) => {
  const { store } = useOrbit()
  const allocation = store.cache.query(q => q.findRecords('allocation'))?.[0]

  return (
    <Container maxWidth="lg">
      <Suspenseful component={CampaignSpend} {...args} allocation={allocation} />
    </Container>
  )
};

const Default: Meta<typeof CampaignSpend> = {
  render: Template,
  parameters: {
    orbit: {
      records: [
        {
          type: "allocations",
          id: "123",
          attributes: {
            year: 2020,
            month: 5,
          },
        },
      ],
    },
  },
  args: {
    onClose: action('on-close'),
    refreshAllocations: action('refresh-allocations'),
    clientTimezone: 'America/Los_Angeles',
  },
}

export const BlankSlate: Meta<typeof CampaignSpend> = {
  ...Default,
  parameters: {
    ...Default.parameters,
    providers: {
      apollo: {
        mocks: [
          {
            request: {
              query: GET_ALLOCATION_QUERY,
              variables: {
                id: "123",
              },
            },
            result: {
              data: {
                allocation: {
                  id: "123",
                  campaignSpends: [],
                }
              },
            },
          },
        ],
      },
    },
  },
  play: async () => {
    const modal = await screen.findByRole('dialog')
    expect(await within(modal).findByRole('button', { name: 'Save' })).toBeDisabled()
    const grid = within(await within(modal).findByRole('grid'))
    let rows = await grid.findAllByRole('row')
    const total = await grid.findByLabelText('Total Spend')

    // Initial state: one blank row
    expect(rows).toHaveLength(1)
    const startDate1 = await within(rows[0]).findByLabelText('Start Date')
    const endDate1 = await within(rows[0]).findByLabelText('End Date')
    const spend1 = await within(rows[0]).findByLabelText('Spend')
    expect(startDate1).toHaveValue('05/01/2020')
    expect(startDate1).toBeDisabled()
    expect(endDate1).toHaveValue('')
    expect(spend1).toHaveValue('')
    expect(total).toHaveTextContent('= $0.00')

    // Fill in the first row
    await userEvent.click(endDate1)
    let datePicker = await screen.findByRole('dialog', { name: 'Select Date Range' })
    await userEvent.click(await within(datePicker).findByText('8'))
    await waitForElementToBeRemoved(() => screen.queryByRole('dialog', { name: 'Select Date Range' }))
    expect(rows).toHaveLength(1)
    await userEvent.type(spend1, '123.45')

    // Now a second row has been added
    rows = await grid.findAllByRole('row')
    expect(rows).toHaveLength(2)
    let startDate2 = await within(rows[1]).findByLabelText('Start Date')
    let endDate2 = await within(rows[1]).findByLabelText('End Date')
    let spend2 = await within(rows[1]).findByLabelText('Spend')
    expect(startDate2).toHaveValue('05/09/2020')
    expect(startDate2).toBeDisabled()
    expect(endDate2).toHaveValue('')
    expect(spend2).toHaveValue('')
    expect(total).toHaveTextContent('= $123.45')

    // Fill in the second row
    await userEvent.click(endDate2)
    datePicker = await screen.findByRole('dialog', { name: 'Select Date Range' })
    await userEvent.click(await within(datePicker).findByText('9'))
    await waitForElementToBeRemoved(() => screen.queryByRole('dialog', { name: 'Select Date Range' }))
    expect(rows).toHaveLength(2)
    await userEvent.type(spend2, '2000')
    await userEvent.tab()
    expect(spend2).toHaveValue('2000.00')

    // Now a third row has been added
    rows = await grid.findAllByRole('row')
    expect(rows).toHaveLength(3)
    const startDate3 = await within(rows[2]).findByLabelText('Start Date')
    const endDate3 = await within(rows[2]).findByLabelText('End Date')
    const spend3 = await within(rows[2]).findByLabelText('Spend')
    expect(startDate3).toHaveValue('05/10/2020')
    expect(startDate3).toBeDisabled()
    expect(endDate3).toHaveValue('')
    expect(spend3).toHaveValue('')
    expect(total).toHaveTextContent('= $2,123.45')

    // Fill in the third row
    await userEvent.click(endDate3)
    datePicker = await screen.findByRole('dialog', { name: 'Select Date Range' })
    await userEvent.click(await within(datePicker).findByText('31'))
    await waitForElementToBeRemoved(() => screen.queryByRole('dialog', { name: 'Select Date Range' }))
    expect(rows).toHaveLength(3)
    await userEvent.type(spend3, '100')
    await userEvent.tab()
    expect(spend3).toHaveValue('100.00')

    // No fourth row has been added because we've selected the last day of the month
    rows = await grid.findAllByRole('row')
    expect(rows).toHaveLength(3)

    // Remove a row
    await userEvent.hover(rows[1])
    await userEvent.click(await within(rows[1]).findByRole('button', { name: 'Remove' }))

    // Now there should only be two rows
    rows = await grid.findAllByRole('row')
    expect(rows).toHaveLength(2)
    startDate2 = await within(rows[1]).findByLabelText('Start Date')
    endDate2 = await within(rows[1]).findByLabelText('End Date')
    spend2 = await within(rows[1]).findByLabelText('Spend')
    expect(startDate2).toHaveValue('05/09/2020')
    expect(startDate2).toBeDisabled()
    expect(endDate2).toHaveValue('05/31/2020')
    expect(spend2).toHaveValue('')
    expect(total).toHaveTextContent('= $123.45')
  },
}

export const WithExistingRecordsDifferentTimezone: Meta<typeof CampaignSpend> = {
  ...Default,
  parameters: {
    ...Default.parameters,
    providers: {
      apollo: {
        mocks: [
          {
            request: {
              query: GET_ALLOCATION_QUERY,
              variables: {
                id: "123",
              },
            },
            result: {
              data: {
                allocation: {
                  __typename: 'Allocation',
                  id: "123",
                  campaignSpends: [
                    {
                      __typename: 'CampaignSpend',
                      id: "111",
                      spendInCents: 100000,
                      startTime: "2020-05-01T07:00:00Z",
                      endTime: "2020-05-08T07:00:00Z",
                    },
                    {
                      __typename: 'CampaignSpend',
                      id: "222",
                      spendInCents: 200000,
                      startTime: "2020-05-08T07:00:00Z",
                      endTime: "2020-05-18T07:00:00Z",
                    },
                  ],
                }
              },
            },
          },
        ],
      },
    },
  },
  play: async () => {
    const modal = await screen.findByRole('dialog')
    expect(await within(modal).findByRole('button', { name: 'Save' })).toBeDisabled()
    const grid = within(await within(modal).findByRole('grid'))
    const rows = await grid.findAllByRole('row')
    const total = await grid.findByLabelText('Total Spend')

    expect(rows).toHaveLength(3)

    // First row
    const startDate1 = await within(rows[0]).findByLabelText('Start Date')
    const endDate1 = await within(rows[0]).findByLabelText('End Date')
    const spend1 = await within(rows[0]).findByLabelText('Spend')
    expect(startDate1).toHaveValue('05/01/2020')
    expect(startDate1).toBeDisabled()
    expect(endDate1).toHaveValue('05/07/2020')
    expect(spend1).toHaveValue('1000.00')

    // Second row
    const startDate2 = await within(rows[1]).findByLabelText('Start Date')
    const endDate2 = await within(rows[1]).findByLabelText('End Date')
    const spend2 = await within(rows[1]).findByLabelText('Spend')
    expect(startDate2).toHaveValue('05/08/2020')
    expect(startDate2).toBeDisabled()
    expect(endDate2).toHaveValue('05/17/2020')
    expect(spend2).toHaveValue('2000.00')

    // Third row has a placeholder
    const startDate3 = await within(rows[2]).findByLabelText('Start Date')
    const endDate3 = await within(rows[2]).findByLabelText('End Date')
    const spend3 = await within(rows[2]).findByLabelText('Spend')
    expect(startDate3).toHaveValue('05/18/2020')
    expect(startDate3).toBeDisabled()
    expect(endDate3).toHaveValue('')
    expect(spend3).toHaveValue('')

    // Total
    expect(total).toHaveTextContent('= $3,000.00')
  },
}
